import Backbone from 'backbone';

import {Sequence} from 'components/Sequence';
import {Strand, StrandOfDoubleStranded} from 'constants/MolbioConstants';
import * as context from 'Context';
import {Enzyme} from 'molbio/enzymes/backbone/Enzymes';
import {Model as Selectable, SelectableAttributes} from 'molbio/maps/selectables/Selectables';

interface CutSiteAttributes extends SelectableAttributes {
  /** The enzyme that is creating the cut site */
  enzyme: Enzyme;
  /** The start position of the cut. This is a model (i.e. 0-indexed) base-pair index. */
  position: number;
  /** Whether the cut site is hovered on a sequence map. */
  hover: boolean;
  /** The background color of the cut site flag in the sequence map; generally `enzyme`'s color if defined */
  color: string | null;
  /** The font color of the cut site flag in the sequence map */
  fontColor: string | null;
  /** A mapping specifying what type of cut site is in what slice.
   * TODO: Enumerate the key types; explain why we keep track of this in state
   */
  sliceData: {
    [sliceViewCid: string]: {
      [key: string]: number;
    };
  };
  /** What strand the cut site is on, e.g. for sticky end enzymes */
  strand: StrandOfDoubleStranded;
}

interface CutSite {
  get<K extends keyof CutSiteAttributes>(key: K): CutSiteAttributes[K];
  set(obj: Partial<CutSiteAttributes>, options?: Backbone.ModelSetOptions): CutSite;
  set<K extends keyof CutSiteAttributes>(key: K, value: CutSiteAttributes[K]): CutSite;
}

class CutSite extends Selectable {
  defaults(): Partial<CutSiteAttributes> {
    return {
      ...super.defaults(),
      hover: false,
      color: null,
      fontColor: '#666',
      sliceData: {},
    };
  }

  initialize(): void {
    if (this.color() != null) {
      this.set({fontColor: 'black'});
    }
    this.listenTo(this.get('enzyme'), 'change:color', (model: Enzyme, newColor: string | null) => {
      this.set({
        color: newColor,
        fontColor: newColor != null ? 'black' : '#666',
      });
    });
  }

  hover(): boolean {
    return this.get('hover');
  }

  sliceData(slice: string, key: string, v?: number): number {
    if (this.get('sliceData')[slice] == null) {
      this.get('sliceData')[slice] = {};
    }
    if (v != null) {
      // if a value is specified, update the data
      this.get('sliceData')[slice][key] = v;
    }
    return this.get('sliceData')[slice][key];
  }

  doubleCutterStart(): number | null {
    const enzyme = this.get('enzyme');
    const isDoubleCutter = enzyme.doubleCutter();
    if (!isDoubleCutter) {
      return null;
    }
    return this.get('position') + this.strand() * (-enzyme.get('cut') + enzyme.get('cut2'));
  }

  position(): number {
    return this.get('position');
  }
  start(): number {
    return this.get('position');
  }
  end(): number {
    return this.start() + this.get('enzyme').get('offset');
  }
  strand(): Strand {
    return this.get('strand');
  }
  name(): string {
    return this.get('enzyme').get('name');
  }
  uid(): string {
    return this.type() + this.cid;
  }
  annularLayer(): number {
    return 0;
  }
  type(): string {
    return 'cutsite';
  }
  visible(): boolean {
    return true;
  }
  color(): string | null {
    return this.get('color');
  }

  click(sequence: Sequence, shiftKey: boolean = false): void {
    const selection = sequence.get('selection');
    if (shiftKey) {
      if (selection.getStartState('cutsite') === this && sequence.circular()) {
        // Shift-clicking on start cutsite should select entire sequence.
        selection.setEndpoints(selection.start(), this.start());
        selection.addEndState({cutsite: this});
      } else if (selection.getStartState('cutsite') != null && !context.get('isLinear')) {
        // Expand clockwise on plasmid
        selection.setEndpoints(selection.start(), this.start());
        selection.addEndState({cutsite: this});
      } else {
        selection.expand(this.start());
        if (selection.freeEnd() === 'start') {
          selection.addStartState({cutsite: this});
        } else {
          selection.addEndState({cutsite: this});
        }
      }
    } else {
      selection.reset(this.start());
      selection.addStartState({cutsite: this});
      sequence.trigger('jump', selection.start());
    }
    this.select({multi: shiftKey});
  }

  dblClick(sequence: Sequence, shiftKey: boolean = false): void {
    this.click(sequence, shiftKey);
    sequence.get('selection').trigger('cutsite', this);
  }
}

export default CutSite;
